import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, ArrowRight, Sparkles, Crown, Users, BookOpen, Zap, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PACKAGE_ICONS = {
  single_class: <BookOpen className="w-6 h-6" />,
  group_session: <Users className="w-6 h-6" />,
  bundle_5day: <Zap className="w-6 h-6" />,
  weekly: <Sparkles className="w-6 h-6" />,
  monthly: <Crown className="w-6 h-6" />,
  star: <Star className="w-6 h-6" />
};

const PACKAGE_COLORS = {
  single_class: { bg: '#F9FAFB', border: '#E5E7EB', accent: '#4B5563', iconBg: '#F3F4F6' },
  group_session: { bg: '#F9FAFB', border: '#E5E7EB', accent: '#4B5563', iconBg: '#F3F4F6' },
  bundle_5day: { bg: '#FFFBEB', border: '#F59E0B', accent: '#92400E', iconBg: '#FEF3C7' },
  weekly: { bg: '#EFF6FF', border: '#002FA7', accent: '#002FA7', iconBg: '#DBEAFE' },
  monthly: { bg: '#0A0A0A', border: '#0A0A0A', accent: '#FFFFFF', iconBg: '#1F2937' },
  star: { bg: '#1E1B4B', border: '#7C3AED', accent: '#FFFFFF', iconBg: '#312E81' }
};

const PACKAGE_LABELS = {
  bundle_5day: 'SAVE $25',
  weekly: 'BEST VALUE',
  monthly: 'MOST POPULAR',
  star: 'STAR DEAL'
};

// Direct Stripe payment links (add more as they become available)
const STRIPE_LINKS = {
  single_class: 'https://buy.stripe.com/14AeVd0Ue4Rx73L8Bi0ZW00',
  group_session: 'https://buy.stripe.com/3cIeVdauOabR4VD5p60ZW02',
  bundle_5day: 'https://buy.stripe.com/00w7sL8mGdo3fAh7xe0ZW03',
  weekly: 'https://buy.stripe.com/28E5kD46q0BhfAh2cU0ZW04',
  monthly: 'https://buy.stripe.com/7sY00jbyScjZ73L9Fm0ZW05',
  star: 'https://buy.stripe.com/9B66oHfP8do3ds99Fm0ZW06',
};

// Static fallback package data for when API is unreachable
const FALLBACK_PACKAGES = [
  { id: 'single_class', name: 'Single Class', description: 'One 1-on-1 tutoring session with Ace Black', price: 15.00, features: ['1 one-on-one session', 'Personalized instruction', 'Session notes & follow-up'] },
  { id: 'group_session', name: 'Group Session', description: 'One group tutoring session with other students', price: 20.00, features: ['1 group session', 'Collaborative learning', 'Peer study environment'] },
  { id: 'bundle_5day', name: '5-Day Bundle', description: '5 one-on-one tutoring sessions at a discounted rate', price: 150.00, features: ['5 one-on-one sessions', 'Save $25 vs single classes', 'Flexible scheduling'] },
  { id: 'weekly', name: 'Weekly Package', description: '3 classes + unlimited group sessions + live chat + full platform access for one week', price: 100.00, features: ['3 one-on-one sessions', 'Unlimited group sessions', 'Live chat & study rooms', 'Video library access', 'AI study assistant'] },
  { id: 'monthly', name: 'Monthly Unlimited', description: 'Unlimited everything for 30 days — the best value', price: 500.00, features: ['Unlimited one-on-one sessions', 'Unlimited group sessions', 'Live chat & study rooms', 'Full video library', 'AI study assistant', 'Priority booking', 'Cancel anytime'] },
  { id: 'star', name: 'Star Package', description: '2 months of unlimited everything — our biggest savings', price: 750.00, features: ['2 full months of access', 'Unlimited one-on-one sessions', 'Unlimited group sessions', 'Full video library & AI tools', 'Priority booking', 'Save $250 vs 2x monthly', 'Cancel anytime'] },
];

export default function Subscribe() {
  const { user, isAuthenticated, credits, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState(FALLBACK_PACKAGES);
  const [loadingPkg, setLoadingPkg] = useState(null);

  const topRowPackages = useMemo(
    () => packages.filter(p => ['single_class', 'group_session', 'bundle_5day'].includes(p.id)),
    [packages]
  );
  const bottomRowPackages = useMemo(
    () => packages.filter(p => ['weekly', 'monthly', 'star'].includes(p.id)),
    [packages]
  );

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/packages`);
        if (data && data.length > 0) setPackages(data);
      } catch (err) {
        // Fallback data already set as initial state
      }
    };
    fetchPackages();
  }, []);

  const handlePurchase = async (packageId) => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }

    setLoadingPkg(packageId);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/packages/checkout`,
        { package_id: packageId, origin_url: window.location.origin },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to start checkout';
      toast.error(typeof msg === 'string' ? msg : 'Checkout failed');
      setLoadingPkg(null);
    }
  };

  const formatPrice = (price) => {
    if (price === Math.floor(price)) return `$${price}`;
    return `$${price.toFixed(2)}`;
  };

  const getPriceLabel = (pkg) => {
    if (pkg.id === 'single_class') return '/class';
    if (pkg.id === 'group_session') return '/session';
    if (pkg.id === 'bundle_5day') return '/bundle';
    if (pkg.id === 'weekly') return '/week';
    if (pkg.id === 'monthly') return '/month';
    if (pkg.id === 'star') return '/2 months';
    return '';
  };

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      {/* Header */}
      <div className="bg-[#F9FAFB] py-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#002FA7] mb-4 block">
            FLEXIBLE PRICING
          </span>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-[#0A0A0A] mb-4">
            Choose Your Plan
          </h1>
          <p className="text-[#4B5563] text-base md:text-lg max-w-2xl mx-auto mb-4">
            From single classes to unlimited access. Pick what works for your schedule and budget.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-sm">
            <span className="flex items-center gap-2 text-[#E53935] font-bold">
              <span className="w-2 h-2 bg-[#E53935] rounded-full animate-pulse" />
              Limited spots available each week
            </span>
            <span className="flex items-center gap-2 text-[#059669] font-bold">
              <CheckCircle className="w-4 h-4" />
              If you don't improve, your next session is free
            </span>
          </div>
        </div>
      </div>

      {/* Credits Banner (if logged in and has credits) */}
      {isAuthenticated && credits && (credits.session_credits > 0 || credits.group_credits > 0 || credits.has_platform_access) && (
        <div className="bg-[#002FA7] text-white py-3 px-6" data-testid="credits-banner">
          <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-6 text-sm">
            {credits.session_credits !== 0 && (
              <span>
                1-on-1 Credits: <strong>{credits.session_credits === -1 ? 'Unlimited' : credits.session_credits}</strong>
              </span>
            )}
            {credits.group_credits !== 0 && (
              <span>
                Group Credits: <strong>{credits.group_credits === -1 ? 'Unlimited' : credits.group_credits}</strong>
              </span>
            )}
            {credits.has_platform_access && (
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Platform Access Active
              </span>
            )}
          </div>
        </div>
      )}

      {/* Pricing Cards */}
      <div className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Top row: Single, Group, 5-Day Bundle */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {topRowPackages.map(pkg => {
              const colors = PACKAGE_COLORS[pkg.id];
              const label = PACKAGE_LABELS[pkg.id];
              return (
                <div
                  key={pkg.id}
                  className="relative border-2 p-6 flex flex-col"
                  style={{ borderColor: colors.border, backgroundColor: colors.bg }}
                  data-testid={`package-${pkg.id}`}
                >
                  {label && (
                    <span
                      className="absolute -top-3 left-6 text-xs font-bold px-3 py-1 tracking-wider"
                      style={{ backgroundColor: colors.border, color: '#FFFFFF' }}
                    >
                      {label}
                    </span>
                  )}
                  <div
                    className="w-12 h-12 flex items-center justify-center mb-4"
                    style={{ backgroundColor: colors.iconBg, color: colors.accent }}
                  >
                    {PACKAGE_ICONS[pkg.id]}
                  </div>
                  <h3 className="text-lg font-bold text-[#0A0A0A] mb-1">{pkg.name}</h3>
                  <p className="text-sm text-[#6B7280] mb-4">{pkg.description}</p>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-3xl font-black text-[#0A0A0A]">{formatPrice(pkg.price)}</span>
                    <span className="text-sm text-[#6B7280]">{getPriceLabel(pkg)}</span>
                  </div>
                  <div className="space-y-2 mb-6 flex-1">
                    {pkg.features.map((f, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: colors.accent }} />
                        <span className="text-sm text-[#374151]">{f}</span>
                      </div>
                    ))}
                  </div>
                  {STRIPE_LINKS[pkg.id] ? (
                    <a href={STRIPE_LINKS[pkg.id]} target="_blank" rel="noopener noreferrer" className="w-full">
                      <Button
                        variant="outline"
                        className="w-full font-bold rounded-sm border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white py-5"
                        data-testid={`buy-${pkg.id}`}
                      >
                        Buy Now
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  ) : (
                    <Button
                      onClick={() => handlePurchase(pkg.id)}
                      disabled={loadingPkg === pkg.id}
                      variant="outline"
                      className="w-full font-bold rounded-sm border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white py-5"
                      data-testid={`buy-${pkg.id}`}
                    >
                      {loadingPkg === pkg.id ? 'Processing...' : (
                        <>
                          Buy Now
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bottom row: Weekly + Monthly + Star (featured) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {bottomRowPackages.map(pkg => {
              const colors = PACKAGE_COLORS[pkg.id];
              const label = PACKAGE_LABELS[pkg.id];
              const isDark = pkg.id === 'monthly' || pkg.id === 'star';
              return (
                <div
                  key={pkg.id}
                  className="relative border-2 p-8 flex flex-col"
                  style={{
                    borderColor: colors.border,
                    backgroundColor: colors.bg,
                    color: isDark ? '#FFFFFF' : '#0A0A0A'
                  }}
                  data-testid={`package-${pkg.id}`}
                >
                  {label && (
                    <span
                      className="absolute -top-3 left-6 text-xs font-bold px-3 py-1 tracking-wider"
                      style={{
                        backgroundColor: pkg.id === 'star' ? '#7C3AED' : isDark ? '#002FA7' : colors.border,
                        color: '#FFFFFF'
                      }}
                    >
                      {label}
                    </span>
                  )}
                  <div
                    className="w-14 h-14 flex items-center justify-center mb-4"
                    style={{ backgroundColor: colors.iconBg, color: isDark ? '#FFFFFF' : colors.accent }}
                  >
                    {PACKAGE_ICONS[pkg.id]}
                  </div>
                  <h3 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-[#0A0A0A]'}`}>
                    {pkg.name}
                  </h3>
                  <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-[#6B7280]'}`}>
                    {pkg.description}
                  </p>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className={`text-4xl font-black ${isDark ? 'text-white' : 'text-[#0A0A0A]'}`}>
                      {formatPrice(pkg.price)}
                    </span>
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-[#6B7280]'}`}>
                      {getPriceLabel(pkg)}
                    </span>
                  </div>
                  <div className="space-y-3 mb-8 flex-1">
                    {pkg.features.map((f, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle
                          className="w-4 h-4 flex-shrink-0 mt-0.5"
                          style={{ color: pkg.id === 'star' ? '#A78BFA' : isDark ? '#60A5FA' : colors.accent }}
                        />
                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-[#374151]'}`}>{f}</span>
                      </div>
                    ))}
                  </div>
                  {STRIPE_LINKS[pkg.id] ? (
                    <a href={STRIPE_LINKS[pkg.id]} target="_blank" rel="noopener noreferrer" className="w-full">
                      <Button
                        className={`w-full font-bold py-6 rounded-sm text-base ${
                          pkg.id === 'star'
                            ? 'bg-[#7C3AED] hover:bg-[#6D28D9] text-white'
                            : 'bg-[#002FA7] hover:bg-[#001B63] text-white'
                        }`}
                        data-testid={`buy-${pkg.id}`}
                      >
                        Start Plan
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </a>
                  ) : (
                    <Button
                      onClick={() => handlePurchase(pkg.id)}
                      disabled={loadingPkg === pkg.id}
                      className={`w-full font-bold py-6 rounded-sm text-base ${
                        pkg.id === 'star'
                          ? 'bg-[#7C3AED] hover:bg-[#6D28D9] text-white'
                          : 'bg-[#002FA7] hover:bg-[#001B63] text-white'
                      }`}
                      data-testid={`buy-${pkg.id}`}
                    >
                      {loadingPkg === pkg.id ? 'Processing...' : (
                        <>
                          Start Plan
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  )}
                  {!isAuthenticated && (
                    <p className={`text-center text-xs mt-3 ${isDark ? 'text-gray-500' : 'text-[#9CA3AF]'}`}>
                      Create an account to purchase
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Comparison section - Simplified Cards */}
      <div className="py-16 px-6 bg-[#F9FAFB]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-[#0A0A0A] text-center mb-3">Not Sure Which Plan?</h2>
          <p className="text-[#4B5563] text-center mb-10">Here's the quick breakdown:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="comparison-cards">
            <div className="bg-white border border-[#E5E7EB] rounded-sm p-6 text-center">
              <p className="text-xs font-bold text-[#4B5563] uppercase tracking-wider mb-3">Just Try It</p>
              <p className="text-3xl font-black text-[#0A0A0A] mb-1">$15</p>
              <p className="text-sm text-[#4B5563] mb-4">Single session</p>
              <ul className="text-sm text-[#4B5563] space-y-2 text-left">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#059669] flex-shrink-0" /> 1 one-on-one session</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#059669] flex-shrink-0" /> Personalized instruction</li>
              </ul>
            </div>
            <div className="bg-[#002FA7] text-white rounded-sm p-6 text-center relative border-2 border-[#002FA7]">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FFC107] text-[#0A0A0A] text-xs font-bold px-3 py-1 tracking-wider">BEST VALUE</span>
              <p className="text-xs font-bold text-white/60 uppercase tracking-wider mb-3">Most Popular</p>
              <p className="text-3xl font-black mb-1">$100<span className="text-lg font-medium">/wk</span></p>
              <p className="text-sm text-white/60 mb-4">3 sessions + extras</p>
              <ul className="text-sm text-white/80 space-y-2 text-left">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#60A5FA] flex-shrink-0" /> 3 one-on-one sessions</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#60A5FA] flex-shrink-0" /> Unlimited groups</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#60A5FA] flex-shrink-0" /> Videos + AI tools</li>
              </ul>
            </div>
            <div className="bg-white border border-[#E5E7EB] rounded-sm p-6 text-center">
              <p className="text-xs font-bold text-[#4B5563] uppercase tracking-wider mb-3">Go Unlimited</p>
              <p className="text-3xl font-black text-[#0A0A0A] mb-1">$500<span className="text-lg font-medium text-[#4B5563]">/mo</span></p>
              <p className="text-sm text-[#4B5563] mb-4">Everything included</p>
              <ul className="text-sm text-[#4B5563] space-y-2 text-left">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#059669] flex-shrink-0" /> Unlimited sessions</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#059669] flex-shrink-0" /> Full platform access</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#059669] flex-shrink-0" /> Priority booking</li>
              </ul>
            </div>
            <div className="bg-[#1E1B4B] text-white rounded-sm p-6 text-center relative border-2 border-[#7C3AED]">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#7C3AED] text-white text-xs font-bold px-3 py-1 tracking-wider">STAR DEAL</span>
              <p className="text-xs font-bold text-white/60 uppercase tracking-wider mb-3">Biggest Savings</p>
              <p className="text-3xl font-black mb-1">$775<span className="text-lg font-medium">/2mo</span></p>
              <p className="text-sm text-white/60 mb-4">Save $250 vs monthly</p>
              <ul className="text-sm text-white/80 space-y-2 text-left">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#A78BFA] flex-shrink-0" /> 2 months unlimited</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#A78BFA] flex-shrink-0" /> All sessions + groups</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#A78BFA] flex-shrink-0" /> Full platform + AI</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
